import type { AbsolutePath } from "shelving";
import { fromProp } from "shelving";
import { MenuButton, Page } from "shared/react";
import { CHALLENGE_MAX, REWARD_TYPES } from "shared/schema";
import { pluralizeCompactQuantity } from "shared/util/number";
import { getMediaImageStyle } from "shared/util/style";
import { ChallengeList } from "../challenge/ChallengeList.js";
import { EntityButton } from "../entity/EntityButton.js";
import { EmbedMarkup } from "../misc/EmbedMarkup.js";
import { useCreatorItem, useEarnedItem } from "../util/data.js";
import { useItem } from "../util/db.js";
import { RewardInteractiveIcon } from "./RewardInteractiveIcon.js";
import { RewardMenu } from "./RewardMenu.js";

export function RewardPage({ id }: { id: string }): React.ReactElement {
	const reward = useItem("reward", id);
	const earned = useEarnedItem(id);
	const { slug, creator, name, about, type, earned: totalEarned, users, hue } = reward.data;
	const username = useCreatorItem(creator).value?.username;
	const path: AbsolutePath = username ? `/${username}/${slug}` : `/reward/${id}`;
	return (
		<Page title={name} path={path}>
			<header className="panel pad-3x" style={getMediaImageStyle(null, hue)}>
				<div>
					<div className="space-xsmall">
						<RewardInteractiveIcon reward={reward.data} earned={earned.value} />
					</div>
					<h1 className="space-none">{name}</h1>
					<p className="label space-none">{REWARD_TYPES[type]}</p>
				</div>
				<MenuButton menu={<RewardMenu id={id} />} mods="button-quiet pin-top pin-right" />
				<EntityButton entity={creator} mods="pin-bottom pin-left size-small" />
			</header>
			{about ? (
				<section>
					<EmbedMarkup>{about}</EmbedMarkup>
				</section>
			) : null}
			<section>
				<h2 className="label">Stats</h2>
				<ul className="status">
					<li>
						Earned {pluralizeCompactQuantity(totalEarned, "time", "times")} by {pluralizeCompactQuantity(users, "person", "people")}
					</li>
				</ul>
			</section>
			<hr />
			<section>
				<h2 className="label">Earned from</h2>
				<ChallengeList
					query={{
						...fromProp(type, id),
						privacy: "public",
						$order: ["!verified", "!started"],
						$limit: CHALLENGE_MAX,
					}}
				/>
			</section>
		</Page>
	);
}
